import React, { useState, useEffect } from "react"
import { X, Calendar, Clock, User, AlertCircle, RefreshCw } from "lucide-react"
import interviewTrackingService from "../services/interviewTrackingService"

// ── Shared style tokens (matching OrgScheduleInterviewForm) ──────────────────
const inputClass =
  "w-full px-3 py-2 h-10 border border-zinc-200 text-[14px] font-medium text-zinc-800 placeholder-zinc-500 focus:outline-none focus:border-zinc-500 transition-colors bg-white hover:border-zinc-300"

const labelClass =
  "block text-[11px] font-bold text-zinc-400 uppercase tracking-widest mb-1.5"

const RescheduleInterviewForm = ({
  isOpen,
  onClose,
  interview,
  onRescheduleComplete,
  interviewersData,
  orgId,
}) => {
  const [date, setDate] = useState("")
  const [startTime, setStartTime] = useState("")
  const [endTime, setEndTime] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [selectedInterviewer, setSelectedInterviewer] = useState("")

  // Initialize form values with the existing interview details if available
  useEffect(() => {
    if (interview && interview.rawDate) {
      const interviewDate = new Date(interview.rawDate)
      const formattedDate = interviewDate.toISOString().split("T")[0]
      setDate(formattedDate)

      const hours = interviewDate.getHours()
      const minutes = interviewDate.getMinutes()
      const formattedStartTime = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`
      setStartTime(formattedStartTime)

      const endDate = new Date(interviewDate)
      endDate.setMinutes(endDate.getMinutes() + 30)
      const endHours = endDate.getHours()
      const endMinutes = endDate.getMinutes()
      const formattedEndTime = `${String(endHours).padStart(2, "0")}:${String(endMinutes).padStart(2, "0")}`
      setEndTime(formattedEndTime)
    }
  }, [interview])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    setErrorMessage("")

    try {
      if (!date || !startTime) {
        throw new Error("Please select both date and time")
      }

      const scheduledDateTime = new Date(`${date}T${startTime}`)

      if (isNaN(scheduledDateTime.getTime())) {
        throw new Error("Invalid date or time")
      }

      if (scheduledDateTime < new Date()) {
        throw new Error("Cannot schedule interviews in the past")
      }

      const success = await interviewTrackingService.rescheduleInterview(
        interview.id,
        scheduledDateTime,
        {
          interviewerEmail: selectedInterviewer,
          date: date,
          endTime: endTime,
          orgId: orgId,
          candidateName: interview.candidateName,
          candidateEmail: interview.candidateEmail,
        },
      )

      if (success) {
        onRescheduleComplete()
      } else {
        throw new Error("Failed to reschedule interview")
      }
    } catch (error) {
      console.error("Error rescheduling interview:", error)
      setErrorMessage(
        error.message || "An error occurred while rescheduling the interview",
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  const today = new Date().toISOString().split("T")[0]

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 backdrop-blur-sm"
      style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
    >
      <style>{`@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&display=swap');`}</style>

      <div
        className="bg-white w-full max-w-xl border border-zinc-200 shadow-2xl flex flex-col max-h-[92vh]"
        style={{ borderRadius: 0 }}
      >
        {/* ── Header ── */}
        <div className="flex justify-between items-center px-7 py-4 border-b border-zinc-200 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-1 h-6 bg-indigo-500" />
            <div>
              <h2 className="text-[13px] font-bold uppercase tracking-widest text-zinc-900">
                Reschedule Interview
              </h2>
              {interview?.title && (
                <p
                  className="text-[13px] text-zinc-700 mt-0.5"
                  style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                >
                  {interview.title}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-7 h-7 border border-zinc-200 hover:border-zinc-400 hover:bg-zinc-50 text-zinc-400 hover:text-zinc-700 transition-all"
            style={{ borderRadius: 0 }}
          >
            <X size={14} />
          </button>
        </div>

        {/* ── Form body ── */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="px-7 py-5 overflow-y-auto flex-1 space-y-5">
            {/* Error banner */}
            {errorMessage && (
              <div
                className="flex items-center gap-2.5 px-4 py-3 bg-red-50 border border-red-200 text-red-600 text-[12px] font-medium"
                style={{ borderRadius: 0 }}
              >
                <AlertCircle size={13} className="flex-shrink-0" />
                {errorMessage}
              </div>
            )}

            {/* ── Interview Title (non-editable) ── */}
            <div>
              <label className={labelClass}>Interview Title</label>
              <input
                type="text"
                value={interview?.title || ""}
                disabled
                className="w-full px-3 py-2 h-10 border border-zinc-200 text-[14px] font-medium text-zinc-400 bg-zinc-50 cursor-not-allowed"
                style={{ borderRadius: 0 }}
              />
            </div>

            {/* ── Interviewer Select ── */}
            <div>
              <label className={labelClass}>
                <User size={11} className="inline mr-1 -mt-0.5" />
                Select Interviewer*
              </label>
              <div className="relative">
                <select
                  value={selectedInterviewer}
                  onChange={(e) => setSelectedInterviewer(e.target.value)}
                  className={`${inputClass} appearance-none pr-8`}
                  style={{ borderRadius: 0 }}
                  required
                >
                  <option value="">Select an interviewer</option>
                  {interviewersData?.map((interviewer) => (
                    <option
                      key={interviewer.uid || `interviewer-${Math.random()}`}
                      value={interviewer.email}
                    >
                      {interviewer.email || "Interviewer"}
                    </option>
                  ))}
                </select>
                {/* Chevron icon */}
                <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    className="text-zinc-400"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>
              </div>
            </div>

            {/* ── Date & Time ── */}
            <div>
              <p className={labelClass}>Date & Time</p>
              <div
                className="grid grid-cols-3 gap-0 border border-zinc-200"
                style={{ borderRadius: 0 }}
              >
                {/* Date */}
                <div className="border-r border-zinc-200">
                  <div className="px-3 py-1.5 border-b border-zinc-100">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">
                      Date
                    </span>
                  </div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                      <Calendar size={13} className="text-zinc-300" />
                    </div>
                    <input
                      type="date"
                      min={today}
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                      required
                      className="w-full pl-9 pr-3 py-2 h-10 text-[13px] text-zinc-900 bg-white focus:outline-none focus:bg-zinc-50 transition-colors border-0"
                      style={{ borderRadius: 0 }}
                    />
                  </div>
                </div>

                {/* Start Time */}
                <div className="border-r border-zinc-200">
                  <div className="px-3 py-1.5 border-b border-zinc-100">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">
                      Start
                    </span>
                  </div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                      <Clock size={13} className="text-zinc-300" />
                    </div>
                    <input
                      type="time"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      required
                      className="w-full pl-9 pr-2 py-2 h-10 text-[13px] text-zinc-900 bg-white focus:outline-none focus:bg-zinc-50 transition-colors border-0"
                      style={{ borderRadius: 0 }}
                    />
                  </div>
                </div>

                {/* End Time */}
                <div>
                  <div className="px-3 py-1.5 border-b border-zinc-100">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">
                      End
                    </span>
                  </div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                      <Clock size={13} className="text-zinc-300" />
                    </div>
                    <input
                      type="time"
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                      required
                      className="w-full pl-9 pr-2 py-2 h-10 text-[13px] text-zinc-900 bg-white focus:outline-none focus:bg-zinc-50 transition-colors border-0"
                      style={{ borderRadius: 0 }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Footer ── */}
          <div className="px-7 py-4 border-t border-zinc-200 flex items-center justify-between shrink-0 bg-zinc-50">
            <p
              className="text-[13px] text-zinc-400"
              style={{ fontFamily: "'IBM Plex Mono', monospace" }}
            >
              🗓 Rescheduling
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="px-4 py-2 text-[11px] font-bold uppercase tracking-widest text-zinc-600 border border-zinc-200 hover:border-zinc-400 hover:text-zinc-900 transition-colors disabled:opacity-40"
                style={{ borderRadius: 0 }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex items-center gap-2 px-5 py-2 text-[11px] font-bold uppercase tracking-widest bg-zinc-900 text-white hover:bg-zinc-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-zinc-900"
                style={{ borderRadius: 0 }}
              >
                {isSubmitting ? (
                  <>
                    <RefreshCw size={12} className="animate-spin" />
                    Rescheduling…
                  </>
                ) : (
                  <>
                    <Calendar size={12} strokeWidth={2.5} />
                    Reschedule Interview
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default RescheduleInterviewForm
